import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  Auth,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  reauthenticateWithCredential,
  sendEmailVerification,
  signInWithEmailAndPassword,
  updatePassword,
  EmailAuthProvider,
  GoogleAuthProvider,
  updateProfile,
  User,
  UserCredential,
} from '@angular/fire/auth';
import { from } from 'rxjs/internal/observable/from';
import { applyActionCode, confirmPasswordReset, sendPasswordResetEmail } from '@firebase/auth';
import { CookieService } from 'ngx-cookie-service';
import { Observable } from 'rxjs/internal/Observable';
import { BehaviorSubject, of, tap } from 'rxjs';
import { signInWithPopup } from 'firebase/auth';
import { map } from 'rxjs/operators';

import { DashboardServiceService } from '@admin/shared/services/dashboard-service.service';

import { ModalServiceService } from '@core/services/modal-service.service';
import { SignoutStateService } from '@core/services/signout-state.service';

import { UserProfileService } from './user.service';

export interface SessionUserCookie {
  uid: string;
  email: string | null;
  emailVerified: boolean;
  displayName: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private auth = inject(Auth); // Lazy inject here
  userData: any; // Save logged in user data
  private authStateUnsubscribe: (() => void) | undefined;
  private authReadyPromise?: Promise<void>;
  twoFaErrorData = new BehaviorSubject<string | null>(null);

  constructor(
    public router: Router,
    private userService: UserProfileService,
    private cookieService: CookieService,
    private dashboardService: DashboardServiceService,
    private modalService: ModalServiceService,
    private signoutState: SignoutStateService
  ) {
    this.initAuthStateListener();
  }

  getUser() {
    return this.auth.currentUser;
  }
  getAuth() {
    return this.auth;
  }

  /** Wait until Firebase has resolved persisted auth (avoids race on refresh/login). */
  waitForAuthReady(): Promise<void> {
    if (!this.authReadyPromise) {
      this.authReadyPromise = new Promise((resolve) => {
        const unsub = onAuthStateChanged(this.auth, () => {
          unsub();
          resolve();
        });
      });
    }
    return this.authReadyPromise;
  }

  /** Keeps cookies in sync when Firebase already has a session (e.g. cookies cleared). */
  async syncSessionFromFirebase(): Promise<boolean> {
    await this.waitForAuthReady();
    const user = this.auth.currentUser;
    if (!user?.emailVerified) {
      return false;
    }
    await this.persistCurrentUser();
    return this.hasValidCookieSession();
  }

  hasValidCookieSession(): boolean {
    const accessToken = this.cookieService.get('accessToken');
    if (!accessToken) {
      return false;
    }
    const userCookie = this.cookieService.get('user');
    if (!userCookie || userCookie === 'null') {
      return false;
    }
    try {
      const parsedUser = JSON.parse(userCookie) as SessionUserCookie;
      return !!parsedUser?.uid && !!parsedUser?.emailVerified;
    } catch {
      return false;
    }
  }

  private serializeUserForCookie(user: User): string {
    const payload: SessionUserCookie = {
      uid: user.uid,
      email: user.email,
      emailVerified: user.emailVerified,
      displayName: user.displayName,
    };
    return JSON.stringify(payload);
  }

  private initAuthStateListener(): void {
    if (this.authStateUnsubscribe) {
      return;
    }
    this.authStateUnsubscribe = onAuthStateChanged(this.auth, async (user) => {
      if (user?.emailVerified) {
        await this.persistCurrentUser();
      }
    });
  }

  /** @deprecated Use init listener; kept for callers that still invoke this. */
  changeAuthState() {
    this.initAuthStateListener();
  }

  getIdToken() {
    return this.auth.currentUser ? this.auth.currentUser.getIdToken() : Promise.resolve(null);
  }

  // Check if user is already authenticated
  isUserAuthenticated(): boolean {
    return this.auth.currentUser !== null;
  }

  // Clear all authentication data before new login
  clearAuthData() {
    this.cookieService.deleteAll('/');
    localStorage.removeItem('user');
    this.userService.setUserSubject(null);
    this.dashboardService.setWorkspace('');
    this.twoFaErrorData.next(null);
  }

  /**
   * Persist current verified user session to cookies immediately.
   * Ensures guards relying on cookies don't bounce the user after login.
   */
  async persistCurrentUser(): Promise<void> {
    await this.waitForAuthReady();
    const user = this.auth.currentUser;
    if (user && user.emailVerified) {
      const token = await user.getIdToken(true);
      this.cookieService.set('accessToken', token, { path: '/' });
      this.cookieService.set('user', this.serializeUserForCookie(user), { path: '/' });
    }
    return Promise.resolve();
  }

  /**
   * Google sign-in: reuse existing Firebase session when already logged in
   * (avoids popup hang / stuck loading on /auth/login).
   */
  async googleSignIn(): Promise<UserCredential> {
    await this.waitForAuthReady();
    const existing = this.auth.currentUser;
    if (existing?.emailVerified) {
      return { user: existing } as UserCredential;
    }
    return signInWithPopup(this.auth, new GoogleAuthProvider());
  }

  // Sign in with email/password
  signIn(email: string, password: string) {
    return from(signInWithEmailAndPassword(this.auth, email, password));
  }

  // Sign up with email/password
  signUp(email: string, password: string, firstName: string, lastName: string) {
    return from(createUserWithEmailAndPassword(this.auth, email, password)).pipe(
      tap(async (result: any) => {
        if (result.user) {
          await updateProfile(result.user, {
            displayName: `${firstName} ${lastName}`,
          });
        }
        this.sendVerificationMail()?.subscribe();
      })
    );
  }

  // Send email verification when new user sign up
  sendVerificationMail() {
    if (this.auth.currentUser) {
      return from(sendEmailVerification(this.auth.currentUser));
    } else {
      return;
    }
  }

  verifyEmail(obbCode: string) {
    return from(applyActionCode(this.auth, obbCode));
  }

  updatePassword(newPassword: string) {
    if (this.auth.currentUser) {
      return updatePassword(this.auth.currentUser, newPassword);
    } else {
      return Promise.reject(new Error('No user is currently signed in.'));
    }
  }

  reauthenticateWithCredentials(password: string) {
    if (this.auth.currentUser) {
      if (this.auth.currentUser.email != null) {
        const credential = EmailAuthProvider.credential(this.auth.currentUser.email, password);
        return reauthenticateWithCredential(this.auth.currentUser, credential);
      } else {
        return Promise.reject(new Error('Current user email is null.'));
      }
    } else {
      return Promise.reject(new Error('No user is currently signed in.'));
    }
  }

  // Reset forgot password
  forgotPassword(passwordResetEmail: string) {
    return sendPasswordResetEmail(this.auth, passwordResetEmail);
  }

  resetPassword(obCode: string, newPassword: string | null) {
    return confirmPasswordReset(this.auth, obCode, <string>newPassword);
  }

  // Returns true when user is logged in and email is verified (cookies or Firebase)
  get isLoggedIn(): boolean {
    if (this.hasValidCookieSession()) {
      return true;
    }
    return !!this.auth.currentUser?.emailVerified;
  }

  getAccessToken(): string | null {
    return this.cookieService.get('accessToken') || null;
  }

  refreshToken(): Observable<{ access_token: string }> {
    const currentUser = this.auth.currentUser;
    if (currentUser) {
      return from(currentUser.getIdTokenResult(true)).pipe(
        map((tokenResult: any) => ({
          access_token: tokenResult.token || tokenResult.access_token,
        }))
      );
    } else {
      return of({ access_token: '' });
    }
  }

  handleError(error: any) {
    let errorMessage = 'An unknown error occurred!';
    if (error.code) {
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'There is no user found with this email. Please try creating new account.';
          break;
        case 'auth/too-many-requests':
          errorMessage =
            'You have already tried too many failed login attempts. Please try again after sometime.';
          break;
        case 'auth/email-already-exists':
          errorMessage = 'This account already exists. Please try login.';
          break;
        case 'auth/invalid-credential':
          errorMessage = 'Incorrect credentials. Please try again';
          break;
        case 'auth/popup-closed-by-user':
          errorMessage = 'Google sign-in dialog was closed before completing the sign-in process.';
          break;
        default:
          errorMessage = error.message;
      }
    }
    this.modalService.openErrorModal(errorMessage);
  }

  setTwoFaStatus(errorData: any) {
    this.twoFaErrorData.next(errorData);
  }
  getTwoFaStatus() {
    return this.twoFaErrorData.asObservable();
  }

  signOut() {
    this.signoutState.isSigningOut = true;
    // Perform the actual Firebase sign-out first
    this.auth
      .signOut()
      .then(() => {
        // Clear local data
        this.cookieService.deleteAll('/');
        localStorage.removeItem('user');
        // Reset app-level state
        this.userService.setUserSubject(null);
        this.dashboardService.setWorkspace('');
        this.twoFaErrorData.next(null);
        // Unsubscribe from auth state changes (if you explicitly subscribed)
        if (this.authStateUnsubscribe) {
          try {
            this.authStateUnsubscribe();
          } catch (error) {
            console.error('Error unsubscribing from auth state:', error);
          }
          this.authStateUnsubscribe = undefined;
        }
        // Navigate after everything is clean
        this.router.navigate(['/auth/login']).finally(() => {
          this.signoutState.isSigningOut = false;
        });
      })
      .catch((error) => {
        this.signoutState.isSigningOut = false;
        this.modalService.openErrorModal('Sign out failed: ' + (error?.message || 'Unknown error'));
      });
  }
}
